/*
 * Iter XX.41 — Gully for Teams (public routes).
 *
 * Three routes exported from this file:
 *   /join/:code          → JoinTeamPage (players see it after the coach shares the link)
 *   /coach/signin        → CoachSignInPage (magic-link redemption)
 *   /coach               → CoachDashboardPage (players joined + weekly activity + top themes + invite artifacts)
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { setToken } from "../lib/nativeTokenStorage";

// ---------------------------------------------------------------------------
// Shared shell — cricket-brand background, no purple, warm.
// ---------------------------------------------------------------------------
function Shell({ children, testid }) {
  return (
    <div
      className="min-h-screen bg-neutral-50 text-neutral-900 flex items-start justify-center px-4 py-12"
      data-testid={testid}
    >
      <div className="max-w-lg w-full">{children}</div>
    </div>
  );
}

function BrandDot() {
  return (
    <div className="flex items-center gap-2 mb-8">
      <span className="inline-block w-2 h-2 bg-neutral-900 rounded-full" />
      <span className="text-xs tracking-[0.15em] uppercase text-neutral-500">Gully</span>
    </div>
  );
}

// ===========================================================================
export function JoinTeamPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, setUser } = useAuth();

  const [team, setTeam] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // New-user form state (only rendered if not logged in).
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/join/${code}`);
        if (alive) { setTeam(data.team); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "not_found");
      }
    })();
    return () => { alive = false; };
  }, [code]);

  async function joinExisting() {
    if (!confirmed) return;
    setBusy(true);
    try {
      await api.post(`/join/${code}/attach`, { confirm: true });
      navigate("/chat?joined=" + encodeURIComponent(team.name));
    } catch (e) {
      alert(e?.response?.data?.detail || "join failed");
    } finally { setBusy(false); }
  }

  async function registerAndJoin() {
    if (!confirmed || !email || !password || !name) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/join/${code}/register-and-attach`, {
        email, name, password, confirm: true,
      });
      if (data.token) await setToken(data.token);
      if (data.user && setUser) setUser(data.user);
      navigate("/chat?joined=" + encodeURIComponent(team.name));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "email_already_registered") {
        alert("This email is already registered. Please sign in first, then re-open the invite link.");
        navigate("/login?return_to=" + encodeURIComponent(`/join/${code}`));
      } else {
        alert(detail || "join failed");
      }
    } finally { setBusy(false); }
  }

  if (err === "not_found" || err === "team_not_found") {
    return (
      <Shell testid="join-page-not-found">
        <BrandDot />
        <h1 className="text-3xl font-light mb-2">Invite link not found</h1>
        <p className="text-neutral-600">
          This link might have expired or been rotated. Ask your coach for a fresh link.
        </p>
      </Shell>
    );
  }
  if (!team || authLoading) {
    return <Shell testid="join-page-loading"><div className="text-neutral-500">…</div></Shell>;
  }

  const cityCountry = [team.city, team.country].filter(Boolean).join(", ");

  return (
    <Shell testid="join-page">
      <BrandDot />
      <p className="label-eyebrow mb-4 text-neutral-500 uppercase text-xs tracking-widest">
        Welcome to
      </p>
      <h1 className="text-4xl sm:text-5xl font-light leading-tight" data-testid="join-team-name">
        {team.name}
      </h1>
      {cityCountry && (
        <p className="text-neutral-500 mt-2" data-testid="join-team-city">{cityCountry}</p>
      )}
      {team.coach_name && (
        <p className="text-neutral-700 mt-6">
          Coach: <span className="font-medium" data-testid="join-coach-name">{team.coach_name}</span>
        </p>
      )}
      {team.welcome_message && (
        <p className="text-neutral-800 mt-6 whitespace-pre-wrap" data-testid="join-welcome-msg">
          {team.welcome_message}
        </p>
      )}
      <p className="text-neutral-500 text-sm mt-8">
        {team.player_count > 0 ? `${team.player_count} players already joined.` : "Be the first player to join."}
      </p>

      <label className="flex items-start gap-3 mt-8 text-sm text-neutral-700" data-testid="join-confirm-row">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-1"
          data-testid="join-confirm-checkbox"
        />
        <span>I confirm I'm a player at <span className="font-medium">{team.name}</span>.</span>
      </label>

      {user ? (
        <button
          onClick={joinExisting}
          disabled={!confirmed || busy}
          className="mt-6 w-full px-4 py-3 rounded bg-neutral-900 text-white text-sm disabled:opacity-40"
          data-testid="join-existing-btn"
        >
          {busy ? "joining…" : "Join Team"}
        </button>
      ) : (
        <div className="mt-6 space-y-3" data-testid="join-new-form">
          <p className="text-neutral-600 text-sm">Create your Gully account to join:</p>
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full bg-white border border-neutral-300 rounded px-3 py-2 text-sm"
            data-testid="join-new-name"
          />
          <input
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" type="email"
            className="w-full bg-white border border-neutral-300 rounded px-3 py-2 text-sm"
            data-testid="join-new-email"
          />
          <input
            value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (min 6)" type="password"
            className="w-full bg-white border border-neutral-300 rounded px-3 py-2 text-sm"
            data-testid="join-new-password"
          />
          <button
            onClick={registerAndJoin}
            disabled={!confirmed || busy || !email || !password || !name}
            className="w-full px-4 py-3 rounded bg-neutral-900 text-white text-sm disabled:opacity-40"
            data-testid="join-new-btn"
          >
            {busy ? "creating…" : "Create account & join"}
          </button>
          <p className="text-neutral-500 text-xs">
            Already have a Gully account?{" "}
            <a
              className="underline"
              href={`/login?return_to=${encodeURIComponent(`/join/${code}`)}`}
              data-testid="join-signin-link"
            >
              Sign in
            </a>{" "}
            first.
          </p>
        </div>
      )}
    </Shell>
  );
}

// ===========================================================================
export function CoachSignInPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState("Signing you in…");

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setErr("no_token"); return; }
    (async () => {
      try {
        const { data } = await api.post("/coach/signin", { token });
        if (data.token) await setToken(data.token);
        setMsg("You're in. Loading your team dashboard…");
        // Fetch /auth/me so useAuth picks the user up.
        try {
          const me = await api.get("/auth/me");
          if (setUser) setUser(me.data);
        } catch (_) { /* soft */ }
        setTimeout(() => navigate("/coach"), 400);
      } catch (e) {
        setErr(e?.response?.data?.detail || "signin_failed");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Shell testid="coach-signin-page">
      <BrandDot />
      {err ? (
        <>
          <h1 className="text-3xl font-light mb-3">Sign-in link is no longer valid</h1>
          <p className="text-neutral-600">
            Reason: <span className="font-mono text-sm">{err}</span>. Ask us to resend the invite.
          </p>
        </>
      ) : (
        <>
          <h1 className="text-3xl font-light mb-3">Welcome, Coach</h1>
          <p className="text-neutral-600" data-testid="coach-signin-msg">{msg}</p>
        </>
      )}
    </Shell>
  );
}

// ===========================================================================
export function CoachDashboardPage() {
  const [team, setTeam] = useState(null);
  const [qr, setQr] = useState(null);
  const [insights, setInsights] = useState(null);
  const [partner, setPartner] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: t }, { data: i }] = await Promise.all([
          api.get("/coach/team"),
          api.get("/coach/team/insights"),
        ]);
        if (!alive) return;
        setTeam(t.team); setQr(t.qr_png_data_uri); setInsights(i);
        setErr(null);
        // Partner rollup is optional — the team may not be enrolled.
        try {
          const { data: p } = await api.get("/coach/partner");
          if (alive) setPartner(p?.enrolled ? p : null);
        } catch (_) { if (alive) setPartner(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "load_failed");
      }
    })();
    return () => { alive = false; };
  }, [rev]);

  const copyInvite = () => {
    if (!team) return;
    const msg = `Join our Gully team\n\n${team.name}${team.city ? " · " + team.city : ""}\nCoach: ${team.coach_name}\n\nJoin: ${team.invite_url}\nCode: ${team.invite_code}`;
    navigator.clipboard?.writeText(msg);
    alert("Invite message copied to clipboard.");
  };

  async function regen() {
    if (!window.confirm("Rotate the invite code? Old link will stop working.")) return;
    setBusy(true);
    try {
      await api.post("/coach/team/regenerate-invite");
      setRev((r) => r + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "rotation failed");
    } finally { setBusy(false); }
  }

  if (err) {
    return (
      <Shell testid="coach-dash-error">
        <BrandDot />
        <h1 className="text-3xl font-light mb-2">No coach team on this account</h1>
        <p className="text-neutral-600">
          If you're a coach, please use the sign-in link Gully emailed to you.
        </p>
      </Shell>
    );
  }
  if (!team || !insights) {
    return <Shell testid="coach-dash-loading"><div className="text-neutral-500">…</div></Shell>;
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 p-6" data-testid="coach-dashboard">
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <BrandDot />
          <h1 className="text-3xl sm:text-4xl font-light" data-testid="coach-team-name">{team.name}</h1>
          <p className="text-neutral-500 mt-1">
            {[team.city, team.country].filter(Boolean).join(", ")}
          </p>
        </div>

        {/* Metric row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="coach-metrics">
          <Metric label="Players joined"        v={insights.players_joined} testid="cm-joined" />
          <Metric label="Weekly active"         v={insights.players_active_7d} testid="cm-active" />
          <Metric label="Video reviews"         v={insights.video_reviews} testid="cm-videos" />
          <Metric label="Match reflections"     v={insights.match_reflections} testid="cm-reflections" />
          <Metric label="Conversations started" v={insights.conversations_started} testid="cm-conv" />
          <Metric
            label="Activation"
            v={`${Math.round((insights.activation_pct || 0) * 100)}%`}
            testid="cm-activation"
          />
        </div>

        {/* Top themes */}
        <div className="bg-white border border-neutral-200 rounded-lg p-5" data-testid="coach-themes">
          <p className="text-xs tracking-[0.15em] uppercase text-neutral-500 mb-3">
            Top themes this week (anonymous)
          </p>
          {insights.top_themes.length === 0 ? (
            <p className="text-neutral-500 text-sm">
              Not enough activity yet. Themes appear once at least three players discuss a topic.
            </p>
          ) : (
            <ul className="space-y-2">
              {insights.top_themes.map((t) => (
                <li key={t.theme} className="flex items-center gap-3 text-sm" data-testid={`coach-theme-${t.theme}`}>
                  <span className="w-32 text-neutral-700 capitalize">{t.theme}</span>
                  <div className="flex-1 h-1.5 bg-neutral-100 rounded overflow-hidden">
                    <div className="h-full bg-neutral-900" style={{ width: `${Math.round(t.share * 100)}%` }} />
                  </div>
                  <span className="text-neutral-500 text-xs tabular-nums w-24 text-right">
                    {t.player_count} players
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-neutral-400 mt-3">
            Individual conversations, memories, and video reviews are never visible to coaches.
          </p>
        </div>

        {/* Partner Program card — only if this team is enrolled */}
        {partner && (
          <div className="bg-white border border-neutral-200 rounded-lg p-5" data-testid="coach-partner-card">
            <p className="text-xs tracking-[0.15em] uppercase text-neutral-500 mb-3">
              Partner Program · estimated partner revenue
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Metric label="Players attributed"  v={partner.players_attributed}   testid="cp-players" />
              <Metric label="Plus members"        v={partner.plus_members}          testid="cp-plus" />
              <Metric label="Conversion"          v={`${Math.round((partner.conversion_rate||0)*100)}%`} testid="cp-conv" />
              <Metric label="Est. monthly revenue"    v={fmtCentsCoach(partner.estimated_monthly_revenue_cents, partner.currency)} testid="cp-monthly" />
              <Metric label="Lifetime net revenue"    v={fmtCentsCoach(partner.lifetime_net_revenue_cents, partner.currency)} testid="cp-lifetime-rev" />
              <Metric label="Est. next payout"        v={fmtCentsCoach(partner.estimated_next_payout_cents, partner.currency)} testid="cp-next-payout" />
            </div>
            {partner.onboarding_url && (
              <div className="mt-4 text-xs">
                <div className="text-neutral-500 mb-1">Your academy's permanent onboarding URL</div>
                <div className="font-mono bg-neutral-100 rounded px-3 py-2 break-all" data-testid="cp-onboarding-url">
                  {partner.onboarding_url}
                </div>
              </div>
            )}
            <p className="text-[10px] text-neutral-500 mt-3">
              All figures are estimates and may change with refunds, chargebacks, or plan changes.
              Payouts are not yet enabled. Estimated Partner Revenue is calculated on net
              revenue actually received by Gully, after discounts, refunds, taxes, and fees.
            </p>
          </div>
        )}

        {/* Invite card */}
        <div className="bg-white border border-neutral-200 rounded-lg p-5" data-testid="coach-invite-card">          <p className="text-xs tracking-[0.15em] uppercase text-neutral-500 mb-3">
            Invite your players
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex-1 space-y-3">
              <div className="text-sm text-neutral-600">Join link</div>
              <div className="font-mono text-sm bg-neutral-100 rounded px-3 py-2 break-all" data-testid="coach-invite-url">
                {team.invite_url}
              </div>
              <div className="text-sm text-neutral-600">Code</div>
              <div className="font-mono text-2xl tracking-[0.2em] bg-neutral-100 rounded px-3 py-3" data-testid="coach-invite-code">
                {team.invite_code}
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={copyInvite}
                  className="px-3 py-2 rounded bg-neutral-900 text-white text-sm"
                  data-testid="coach-copy-invite-btn"
                >
                  Copy invite message
                </button>
                <button
                  onClick={regen}
                  disabled={busy}
                  className="px-3 py-2 rounded border border-neutral-300 text-neutral-700 text-sm disabled:opacity-40"
                  data-testid="coach-rotate-btn"
                >
                  Rotate code
                </button>
              </div>
            </div>
            {qr && (
              <div className="flex-shrink-0 self-start" data-testid="coach-qr-wrap">
                <img
                  src={qr}
                  alt="QR code"
                  width="160" height="160"
                  className="border border-neutral-200 rounded"
                  data-testid="coach-qr-img"
                />
                <a
                  href={qr} download={`gully-${team.invite_code}-qr.png`}
                  className="block text-[11px] text-neutral-500 underline text-center mt-2"
                  data-testid="coach-qr-download"
                >
                  Download QR PNG
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Metric({ label, v, testid }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid={testid}>
      <div className="text-[10px] tracking-[0.15em] uppercase text-neutral-500">{label}</div>
      <div className="text-2xl font-light tabular-nums mt-1">{v ?? "—"}</div>
    </div>
  );
}

function fmtCentsCoach(cents, currency = "USD") {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString(undefined, {
    style: "currency", currency, minimumFractionDigits: 0,
  });
}
